import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Eye, Heart, Megaphone, BookOpen, Gift, GraduationCap, Music } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import useAccessMode from '@/lib/useAccessMode';

const C = {
  bg: '#0A0F1E', surface: '#111827', card: '#1A2236',
  gold: '#D4A853', goldLight: '#F5D799', teal: '#38BDF8',
  cream: '#F8F5EE', muted: '#94A3B8', white: '#FFFFFF',
};

const ROLES = [
  { id: 'parent', label: 'Parents', icon: Heart, color: '#D4A853', bg: 'rgba(212,168,83,0.10)', apiRole: 'guardian' },
  { id: 'teacher', label: 'Teachers', icon: BookOpen, color: '#A78BFA', bg: 'rgba(167,139,250,0.10)', apiRole: 'teacher' },
  { id: 'brand', label: 'Brands', icon: Megaphone, color: '#F472B6', bg: 'rgba(244,114,182,0.10)', apiRole: 'brand_partner' },
  { id: 'artist', label: 'Artists', icon: Music, color: '#34D399', bg: 'rgba(52,211,153,0.10)', apiRole: 'artist' },
];

const inputStyle = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  color: C.cream,
};

const GuardianRegister = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { mode: accessMode, termsText } = useAccessMode();
  const inviteOnly = accessMode === 'invite_only';
  const closed = accessMode === 'closed';

  const roleParam = searchParams.get('role');
  const initialRole = roleParam === 'brand_partner' ? 'brand' : roleParam === 'artist' ? 'artist' : roleParam === 'teacher' ? 'teacher' : 'parent';
  const enrollmentCode = searchParams.get('enrollment');

  const [activeRole, setActiveRole] = useState(initialRole);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: searchParams.get('ref') || '',
    schoolName: '',
    inviteCode: searchParams.get('invite') || '',
    acceptTerms: false,
  });
  const [showTerms, setShowTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);

  // Fetch enrollment details if code is present in URL
  useEffect(() => {
    if (!enrollmentCode) return;
    setEnrollmentLoading(true);
    authAPI.getEnrollment(enrollmentCode)
      .then((res) => {
        setEnrollmentData(res.data);
        setFormData((prev) => ({ ...prev, email: res.data.parent_email || '' }));
        setActiveRole('parent');
      })
      .catch(() => {
        toast.error('Invalid or expired enrollment code');
      })
      .finally(() => setEnrollmentLoading(false));
  }, [enrollmentCode]);

  const activeConfig = ROLES.find(r => r.id === activeRole);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('auth.passwordMismatch'));
      return;
    }
    if (formData.password.length < 6) {
      toast.error(t('auth.passwordTooShort'));
      return;
    }
    if (inviteOnly && !formData.inviteCode.trim()) {
      toast.error('Please enter your invite code.');
      return;
    }
    if (inviteOnly && !formData.acceptTerms) {
      toast.error('Please accept the pilot terms to continue.');
      return;
    }
    setLoading(true);

    // If this is an enrollment claim, use the claim endpoint
    if (enrollmentCode && enrollmentData) {
      try {
        const res = await authAPI.claimEnrollment({
          enrollment_code: enrollmentCode,
          email: formData.email,
          full_name: formData.fullName,
          password: formData.password,
          access_code: inviteOnly ? formData.inviteCode.trim() : undefined,
          accept_pilot_terms: inviteOnly ? formData.acceptTerms : false,
        });
        const { access_token, user: claimedUser, students_claimed } = res.data;
        // Store token and redirect
        localStorage.setItem('token', access_token);
        const names = students_claimed.map((s) => s.full_name).join(', ');
        toast.success(`Welcome! You've claimed ${students_claimed.length} student(s): ${names}`);
        navigate('/portal');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Enrollment claim failed');
      }
      setLoading(false);
      return;
    }

    // For teachers, register as guardian with is_teacher flag
    const apiRole = activeRole === 'teacher' ? 'guardian' : activeConfig.apiRole;
    const result = await register(
      formData.fullName, formData.email, formData.password,
      activeRole === 'parent' ? (formData.referralCode || undefined) : undefined,
      apiRole,
      activeRole === 'teacher' ? true : false,
      activeRole === 'teacher' ? formData.schoolName : '',
      inviteOnly ? { code: formData.inviteCode.trim(), acceptTerms: formData.acceptTerms } : null
    );
    if (result.success) {
      if (activeRole === 'brand') {
        toast.success(t('auth.brandPartnerCreated'));
        navigate('/brand-portal');
      } else if (activeRole === 'artist') {
        toast.success('Artist account created! Complete your profile to get started.');
        navigate('/artist-portal');
      } else if (activeRole === 'teacher') {
        toast.success('Teacher account created! Welcome to Semantic Vision.');
        navigate('/portal');
      } else {
        toast.success(formData.referralCode ? t('auth.referralApplied') : t('auth.accountCreated'));
        navigate('/portal');
      }
    } else {
      toast.error(result.error || t('auth.registrationFailed'));
    }
    setLoading(false);
  };

  const titles = {
    parent: { heading: t('auth.createAccount'), sub: t('auth.startVocabJourney') },
    teacher: { heading: t('auth.teacherRegistration'), sub: t('auth.createClassroomAccount') },
    brand: { heading: t('auth.brandPartnerReg'), sub: t('auth.brandPartnerDesc') },
  };

  const placeholders = {
    parent: { name: 'John Doe', email: 'parent@example.com' },
    teacher: { name: 'Ms. Johnson', email: 'teacher@school.edu' },
    brand: { name: 'Company Name', email: 'brand@company.com' },
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: C.bg, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(212,168,83,0.12)' }}>
        <button onClick={() => navigate('/')} className="flex items-center gap-3 group" data-testid="nav-logo">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${C.gold}, ${C.teal})` }}>
            <Eye size={22} className="text-black" />
          </div>
          <span className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Sora', sans-serif", color: C.cream }}>
            {t('landing.title')}
          </span>
        </button>
        <LanguageSwitcher />
      </nav>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-4 py-8">
        <div className="w-full max-w-md p-8 rounded-2xl" style={{ background: C.card, border: '1px solid rgba(255,255,255,0.08)' }}>

          {/* Enrollment Banner */}
          {enrollmentLoading && (
            <div className="mb-6 p-4 rounded-xl text-center" style={{ background: 'rgba(212,168,83,0.12)', border: '1px solid rgba(212,168,83,0.3)' }}>
              <p className="text-sm font-medium" style={{ color: C.gold }}>Loading enrollment details...</p>
            </div>
          )}
          {enrollmentData && (
            <div className="mb-6 p-4 rounded-xl" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)' }}>
              <div className="flex items-center gap-2 mb-2">
                <GraduationCap size={18} style={{ color: '#10B981' }} />
                <span className="text-sm font-bold" style={{ color: '#10B981' }}>Enrollment Invitation</span>
              </div>
              <p className="text-sm font-medium" style={{ color: C.cream }}>
                Your child(ren) <strong style={{ color: C.goldLight }}>{enrollmentData.student_names.join(', ')}</strong> are
                enrolled at <strong style={{ color: C.goldLight }}>{enrollmentData.school_name}</strong>
                {enrollmentData.teacher_name ? ` by ${enrollmentData.teacher_name}` : ''}.
              </p>
              <p className="text-xs mt-1" style={{ color: C.muted }}>Complete your registration below to access their profile.</p>
            </div>
          )}

          {/* Role Selector at TOP */}
          {!enrollmentData && (
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-center mb-3" style={{ color: C.muted }}>
              I am a...
            </p>
            <div className="grid grid-cols-3 gap-2" data-testid="register-role-selector">
              {ROLES.map((role) => {
                const Icon = role.icon;
                const isActive = activeRole === role.id;
                return (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => setActiveRole(role.id)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all hover:scale-105"
                    style={{
                      background: isActive ? `${role.color}20` : 'rgba(255,255,255,0.03)',
                      border: isActive ? `2px solid ${role.color}` : '1px solid rgba(255,255,255,0.08)',
                      transform: isActive ? 'scale(1.05)' : 'scale(1)',
                    }}
                    data-testid={`register-role-${role.id}`}
                  >
                    <Icon size={20} style={{ color: isActive ? role.color : C.muted }} />
                    <span className="text-xs font-semibold" style={{ color: isActive ? role.color : C.muted }}>
                      {role.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* Dynamic Header */}
          <div className="text-center mb-6">
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3 transition-all"
              style={{ background: `${activeConfig.color}20` }}
            >
              <activeConfig.icon size={28} style={{ color: activeConfig.color }} />
            </div>
            <h2 className="text-xl font-bold" style={{ fontFamily: "'Sora', sans-serif", color: C.cream }} data-testid="register-title">
              {titles[activeRole].heading}
            </h2>
            <p className="mt-1 text-sm" style={{ color: C.muted }}>{titles[activeRole].sub}</p>
          </div>

          {closed && (
            <div className="p-4 rounded-xl text-sm mb-2" style={{ background: 'rgba(212,168,83,0.1)', border: '1px solid rgba(212,168,83,0.3)', color: C.cream }} data-testid="signup-closed">
              Semantic Vision is a private pilot and is not accepting new accounts right now.
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4" hidden={closed}>
            {inviteOnly && (
              <div className="p-4 rounded-xl space-y-3" style={{ background: 'rgba(212,168,83,0.08)', border: '1px solid rgba(212,168,83,0.3)' }} data-testid="invite-block">
                <p className="text-sm" style={{ color: C.cream }}>Semantic Vision is an invite-only pilot. Enter the invite code you were given.</p>
                <input type="text" required value={formData.inviteCode}
                  onChange={(e) => setFormData({ ...formData, inviteCode: e.target.value.toUpperCase() })}
                  placeholder="INV-XXXXXXXX" autoComplete="off"
                  className="w-full px-4 py-3 rounded-xl text-sm font-mono font-bold outline-none"
                  style={inputStyle} data-testid="invite-code-input" />
                <label className="flex items-start gap-2 text-xs" style={{ color: C.muted }}>
                  <input type="checkbox" checked={formData.acceptTerms} className="mt-0.5"
                    onChange={(e) => setFormData({ ...formData, acceptTerms: e.target.checked })} data-testid="accept-pilot-terms" />
                  <span>
                    I have read and accept the pilot terms: this software is confidential and for evaluation only.{' '}
                    <button type="button" className="underline" style={{ color: C.gold }} onClick={() => setShowTerms(!showTerms)}>
                      {showTerms ? 'Hide' : 'Read'} the terms
                    </button>
                  </span>
                </label>
                {showTerms && <p className="text-xs leading-relaxed" style={{ color: C.muted }} data-testid="pilot-terms-text">{termsText}</p>}
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: C.muted }}>{t('common.fullName')}</label>
              <input type="text" required value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                placeholder={placeholders[activeRole].name}
                className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all focus:ring-2"
                style={inputStyle} data-testid="register-name" />
            </div>
            {activeRole === 'teacher' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: C.muted }}>School Name</label>
                <input type="text" required value={formData.schoolName}
                  onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
                  placeholder="Lincoln Elementary School"
                  className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all focus:ring-2"
                  style={inputStyle} data-testid="register-school" />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: C.muted }}>{t('common.email')}</label>
              <input type="email" required value={formData.email}
                onChange={(e) => !enrollmentData && setFormData({ ...formData, email: e.target.value })}
                readOnly={!!enrollmentData}
                placeholder={placeholders[activeRole].email}
                className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all focus:ring-2"
                style={{ ...inputStyle, ...(enrollmentData ? { opacity: 0.7, cursor: 'not-allowed' } : {}) }}
                data-testid="register-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: C.muted }}>{t('common.password')}</label>
              <input type="password" required value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Min 6 characters"
                className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all focus:ring-2"
                style={inputStyle} data-testid="register-password" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: C.muted }}>{t('common.confirmPassword')}</label>
              <input type="password" required value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Confirm password"
                className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all focus:ring-2"
                style={inputStyle} data-testid="register-confirm-password" />
            </div>

            {/* Referral code — only for parents */}
            {activeRole === 'parent' && (
              <div className="p-4 rounded-xl" style={{
                background: formData.referralCode ? 'rgba(16,185,129,0.08)' : 'transparent',
                border: formData.referralCode ? '1px solid rgba(16,185,129,0.3)' : 'none',
              }}>
                <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: C.muted }}>
                  <Gift size={14} style={{ color: C.teal }} /> {t('auth.referralCode')} ({t('common.optional')})
                </label>
                <input type="text" value={formData.referralCode}
                  onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
                  placeholder="REF-XXXXXX"
                  className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all focus:ring-2"
                  style={inputStyle} data-testid="referral-code-input" />
                {formData.referralCode && (
                  <p className="text-xs font-semibold mt-2" style={{ color: '#10B981' }}>{t('auth.referralBonus')}</p>
                )}
              </div>
            )}

            <button type="submit" disabled={loading}
              className="w-full py-3.5 rounded-xl text-base font-bold text-black transition-all duration-300 hover:scale-[1.02] hover:shadow-lg disabled:opacity-50 mt-2"
              style={{ background: `linear-gradient(135deg, ${activeConfig.color}, ${C.goldLight})` }}
              data-testid="register-submit">
              {loading ? t('common.creatingAccount') : t('common.register')}
            </button>

            <div className="text-center pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-sm" style={{ color: C.muted }}>
                {t('common.hasAccount')}{' '}
                <Link
                  to={activeRole === 'brand' ? '/login?type=brand' : activeRole === 'artist' ? '/login?type=artist' : activeRole === 'teacher' ? '/login?type=teacher' : '/login'}
                  className="font-semibold hover:underline"
                  style={{ color: C.gold }}
                  data-testid="login-link"
                >
                  {t('common.loginHere')}
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GuardianRegister;
